import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { Role } from '@/types/api';
import { Spinner } from '@/components/ui/Spinner';

interface ProtectedRouteProps {
  roles?: Role[];
  children: ReactNode;
}

export function ProtectedRoute({ roles, children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isInitializing, hasRole, activeOrgId, organizations } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }

  if (roles && !hasRole(roles)) {
    return <Navigate to="/forbidden" replace />;
  }

  // Redirect client users with multiple orgs who haven't selected one yet
  const isClientRole = user.role === 'client_admin' || user.role === 'client_user';
  if (
    isClientRole &&
    organizations.length > 1 &&
    !activeOrgId &&
    location.pathname !== '/select-org'
  ) {
    return <Navigate to="/select-org" replace />;
  }

  return <>{children}</>;
}
